// grounding.ts — the vocabulary of the needs-review gate, and the ONE place it
// is defined.
//
// Five lanes speak this grammar: PO import (Procurement/PoImportDocumentCard),
// PO scan (Procurement/PoScanPage), quote-to-draft-PO
// (Procurement/QuoteDraftPage), catalog import (Parts/CatalogImportPage) and
// packing list (Receiving/PackingListPage). It used to live mid-file in
// Procurement/poImportApi.ts because the import lane got there first, which left
// the layering upside down: a shared component describing a shared gate had to
// import one lane's contract module to name its own types.
//
// So the grammar lives here, and each lane's contract module imports from here
// and re-exports whatever its own pages, draft module and tests already read off
// it. Those re-exports are the lane-facade convention, not shims — a lane is
// still the right place to ask "what does MY parser return".
//
// The gate itself is server-computed (api/src/procurement/po-import-service.js
// computeReviewRequired, and the same code behind every other lane's parser), so
// the key, the resolution vocabulary and the labels must be one definition or a
// screen and the server can disagree about what is still outstanding.

// ---------------------------------------------------------------------------
// The not-sure gate (po-import-service.js, "Not sure blocks until a human
// reconciles it"). An AI-read document carries an explicit list of places a
// person must look at; confirm() refuses the batch until each one has been
// either edited or affirmatively confirmed. xlsx documents always get [].
// ---------------------------------------------------------------------------

export type ReviewTarget = 'document' | 'field' | 'line';

export type ReviewReason =
  | 'low_confidence'
  | 'unverified_text'
  | 'ungrounded_scan'
  | 'po_number_mismatch'
  | 'ai_remark'
  | 'model_not_sure';

export interface ReviewRequiredEntry {
  target: ReviewTarget;
  field: string | null;
  line_number: number | null;
  reason: ReviewReason;
  message: string;
}

export type ReviewResolution = 'edited' | 'confirmed_as_staged';

export interface ConfirmReviewInput {
  target: ReviewTarget;
  field?: string | null;
  line_number?: number | null;
  resolution: ReviewResolution;
}

// What a person clears with ONE action. Mirrors po-import-service.js reviewKey
// EXACTLY: the reason never keys, and a line's field never keys either, because
// description and part_number on one row are read in one glance.
export function reviewKey(entry: {
  target: ReviewTarget;
  field?: string | null;
  line_number?: number | null;
}): string {
  const line = entry.line_number === null || entry.line_number === undefined ? '' : entry.line_number;
  const field = entry.target === 'line' ? '' : entry.field || '';
  return `${entry.target}|${field}|${line}`;
}

export const REVIEW_REASON_LABELS: Record<ReviewReason, string> = {
  low_confidence: "AI wasn't sure",
  unverified_text: 'Not found on the page',
  ungrounded_scan: 'Nothing could be checked',
  po_number_mismatch: 'Two different numbers',
  ai_remark: 'The reader left a note',
  model_not_sure: "AI wasn't sure",
};

export const REVIEW_PLACE_LABELS: Record<string, string> = {
  po_number: 'purchase-order number',
  order_date: 'order date',
  quote_number: 'quote number',
  ship_date: 'ship date',
  ship_via: 'ship via',
  terms: 'terms',
  vendor: 'vendor',
  'vendor.name': 'vendor name',
  ship_to: 'ship-to block',
  lines: 'line items',
  subtotal_cents: 'subtotal',
  // Quote lane (quoteApi.ts). Additive: the import and scan lanes never emit
  // these fields, and an unmapped one would fall back to the raw column name —
  // "this expires_on" is not a phrase to put in front of a buyer.
  quote_date: 'quote date',
  expires_on: 'expiration date',
  currency: 'currency',
  fees: 'fees',
};

export function slotLabel(target: ReviewTarget, field: string | null, lineNumber: number | null): string {
  if (target === 'line') return `line ${lineNumber}`;
  if (target === 'document') return 'this document';
  return REVIEW_PLACE_LABELS[field || ''] || field || 'this field';
}
